import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@devhop/ui";
import { Check, ChevronsUpDown, LayoutDashboard, Package } from "lucide-react";
import {
  type FreedomApp,
  type FreedomAppId,
  getFreedomApp,
  getFreedomApps,
} from "@/shared/lib/freedom-apps";

type AppSwitcherProps = {
  currentApp: FreedomAppId;
};

const appIcons: Record<FreedomAppId, typeof LayoutDashboard> = {
  erp: LayoutDashboard,
  stock: Package,
};

function AppMenuItem({
  app,
  currentApp,
}: {
  app: FreedomApp;
  currentApp: FreedomAppId;
}) {
  const Icon = appIcons[app.id];
  const isCurrent = app.id === currentApp;

  if (isCurrent) {
    return (
      <DropdownMenuItem disabled className="gap-2 p-2 opacity-100">
        <div className="flex size-6 items-center justify-center rounded-sm border bg-sidebar-primary text-sidebar-primary-foreground">
          <Icon className="size-3.5 shrink-0" />
        </div>
        <div className="grid flex-1 text-start leading-tight">
          <span className="truncate font-medium">{app.subtitle}</span>
        </div>
        <Check className="ms-auto size-4 text-foreground" />
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem asChild className="gap-2 p-2">
      <a href={`${app.href}/app/dashboard`}>
        <div className="flex size-6 items-center justify-center rounded-sm border">
          <Icon className="size-3.5 shrink-0" />
        </div>
        <div className="grid flex-1 text-start leading-tight">
          <span className="truncate font-medium">{app.subtitle}</span>
        </div>
      </a>
    </DropdownMenuItem>
  );
}

export function AppSwitcher({ currentApp }: AppSwitcherProps) {
  const { isMobile } = useSidebar();
  const activeApp = getFreedomApp(currentApp);
  const apps = getFreedomApps();

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <img
                src="/assets/favicon_io/android-chrome-192x192.png"
                alt="freedom®"
                className="size-8 rounded-sm"
              />
              <div className="grid flex-1 text-start text-sm leading-tight">
                <span className="truncate font-semibold">{activeApp.name}</span>
                <span className="truncate text-xs text-sidebar-foreground/70">
                  {activeApp.subtitle}
                </span>
              </div>
              <ChevronsUpDown className="ms-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              ແອັບພລິເຄຊັນ
            </DropdownMenuLabel>
            {apps.map((app) => (
              <AppMenuItem key={app.id} app={app} currentApp={currentApp} />
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="font-normal text-muted-foreground text-xs">
              SSO — ເຂົ້າລະບົບຄັ້ງດຽວໃຊ້ໄດ້ທັງສອງແອັບ
            </DropdownMenuLabel>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
